import type { Theme } from '@emotion/react'
import { css } from '@emotion/react'

import { breakpoint } from '@twistopayments/design-system/styles'

export const root = css`
  margin: 0;
  display: block;
`

const px = (n: number) => `${n}px`
const lh = (n: number | 'auto') => (n === 'auto' ? 'normal' : `${n}px`)

// Variant keys mirror the existing component API — mapped to Figma App ramp tokens:
//   h1/h1Mobile/h2/h3 → app.h1…h3
//   b1 → app.title (18px), b2 → app.body (17px), b3 → app.bodySm (14px)
//   b4 → app.caption (13px), b5 → app.overline (11px)
//   bodyMedium / bodySmall → app.body, bodyMediumMobile → app.bodyMobile
export type TypographyVariant =
  | 'h1'
  | 'h1Mobile'
  | 'h2'
  | 'h3'
  | 'b1'
  | 'b2'
  | 'b3'
  | 'b4'
  | 'b5'
  | 'bodyMedium'
  | 'bodyMediumMobile'
  | 'bodySmall'

export type FontWeightKey = 'light' | 'regular' | 'medium' | 'heavy' | 'bold'

export const typographyVariant = (theme: Theme) => {
  const { app } = theme.typography

  const variants = {
    h1: css`
      font-size: ${px(app.h1.fontSize)};
      line-height: ${lh(app.h1.lineHeight)};
      letter-spacing: ${px(app.h1.letterSpacing)};
    `,
    h1Mobile: css`
      font-size: ${px(app.h1Mobile.fontSize)};
      line-height: ${lh(app.h1Mobile.lineHeight)};
      letter-spacing: ${px(app.h1Mobile.letterSpacing)};
    `,
    h2: css`
      font-size: ${px(app.h2.fontSize)};
      line-height: ${lh(app.h2.lineHeight)};
      letter-spacing: ${px(app.h2.letterSpacing)};
    `,
    h3: css`
      font-size: ${px(app.h3.fontSize)};
      line-height: ${lh(app.h3.lineHeight)};
      letter-spacing: ${px(app.h3.letterSpacing)};
    `,
    b1: css`
      font-size: ${px(app.title.fontSize)};
      line-height: ${lh(app.title.lineHeight)};
      letter-spacing: ${px(app.title.letterSpacing)};
    `,
    b2: css`
      font-size: ${px(app.body.fontSize)};
      line-height: ${lh(app.body.lineHeight)};
      letter-spacing: ${px(app.body.letterSpacing)};
    `,
    b3: css`
      font-size: ${px(app.bodySm.fontSize)};
      line-height: ${lh(app.bodySm.lineHeight)};
      letter-spacing: ${px(app.bodySm.letterSpacing)};
    `,
    b4: css`
      font-size: ${px(app.caption.fontSize)};
      line-height: ${lh(app.caption.lineHeight)};
      letter-spacing: ${px(app.caption.letterSpacing)};
    `,
    b5: css`
      font-size: ${px(app.overline.fontSize)};
      line-height: ${lh(app.overline.lineHeight)};
      letter-spacing: ${px(app.overline.letterSpacing)};
    `,
    bodyMedium: css`
      font-size: ${px(app.body.fontSize)};
      line-height: ${lh(app.body.lineHeight)};
      letter-spacing: ${px(app.body.letterSpacing)};
    `,
    bodyMediumMobile: css`
      font-size: ${px(app.bodyMobile.fontSize)};
      line-height: ${lh(app.bodyMobile.lineHeight)};
      letter-spacing: ${px(app.bodyMobile.letterSpacing)};
    `,
    bodySmall: css`
      font-size: ${px(app.body.fontSize)};
      line-height: ${lh(app.body.lineHeight)};
      letter-spacing: ${px(app.body.letterSpacing)};
    `,
  }

  for (const variant of Object.keys(variants) as TypographyVariant[]) {
    const metrics = theme.typography.variants?.[variant]
    if (metrics) {
      const { mobileLetterSpacing, ...desktop } = metrics
      variants[variant] = css(desktop, mobileLetterSpacing && {
        [breakpoint.down('sm')]: { letterSpacing: mobileLetterSpacing },
      })
    }
  }

  return variants
}

export const fontWeight = (theme: Theme) => {
  const fw = theme.typography.fontWeight

  return {
    light: css`
      font-weight: ${fw.light};
    `,
    regular: css`
      font-weight: ${fw.regular};
    `,
    medium: css`
      font-weight: ${fw.medium};
    `,
    /** @deprecated use 'bold' */
    heavy: css`
      font-weight: ${fw.bold};
    `,
    bold: css`
      font-weight: ${fw.bold};
    `,
  }
}

export const color = (theme: Theme) => ({
  textPrimary: css`
    color: ${theme.palette.text.title};
  `,
  textSecondary: css`
    color: ${theme.palette.text.description};
  `,
  mediumContrast: css`
    color: ${theme.palette.ghost.mediumContrast};
  `,
  textTertiary: css`
    color: ${theme.palette.tertiary.main};
  `,
  textHighlight: css`
    color: ${theme.palette.secondary.mainContrast};
  `,
  primary: css`
    color: ${theme.palette.primary.mainContrast};
  `,
  success: css`
    color: ${theme.palette.success.main};
  `,
  secondary: css`
    color: ${theme.palette.text.description};
  `,
  warning: css`
    color: ${theme.palette.warning.main};
  `,
  error: css`
    color: ${theme.palette.error.main};
  `,
  textBody: css`
    color: ${theme.palette.text.body};
  `,
})

export const align = (align: 'start' | 'center' | 'end') => {
  if (align === 'start') {
    return css`
      text-align: left;
    `
  }

  if (align === 'end') {
    return css`
      text-align: right;
    `
  }

  return css`
    text-align: center;
  `
}

export const textTransform = {
  none: css`
    text-transform: none;
  `,
  uppercase: css`
    text-transform: uppercase;
  `,
  lowercase: css`
    text-transform: lowercase;
  `,
  capitalize: css`
    text-transform: capitalize;
  `,
  lineThrough: css`
    text-decoration: line-through;
  `,
}

export const inline = css`
  display: inline;
`

export const noWrap = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const gutterBottom = css`
  margin-bottom: 0.3em;
`
